import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    alpha,
    Avatar,
    Box,
    Button,
    Chip,
    CircularProgress,
    Collapse,
    Container,
    Grid,
    Paper,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TablePagination,
    TextField,
    Typography,
} from "@mui/material";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import Popup from "../../components/Popup";
import { StyledTableCell, StyledTableRow } from "../../components/styles";
import { getTeacherComplains } from "../../redux/complainRelated/complainHandle";
import { addStuff } from "../../redux/userRelated/userHandle";
import { underControl } from "../../redux/userRelated/userSlice";
import { getSchoolIdForUser } from "../../utils/userContext";

const formatDate = (d) => {
    const date = new Date(d);
    return date.toString() !== "Invalid Date"
        ? date.toLocaleDateString(undefined, { dateStyle: "medium" })
        : "—";
};

const TeacherComplain = () => {
    const dispatch = useDispatch();
    const { currentUser, status, error } = useSelector((s) => s.user);
    const { complainsList, loading, error: listError } = useSelector((s) => s.complain);

    const teacherId = currentUser?._id;
    const schoolId = getSchoolIdForUser(currentUser);

    const [complaint, setComplaint] = useState("");
    const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [submitting, setSubmitting] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const [message, setMessage] = useState("");
    const [showForm, setShowForm] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);

    const reload = useCallback(() => {
        if (teacherId) dispatch(getTeacherComplains(teacherId));
    }, [dispatch, teacherId]);

    useEffect(() => {
        reload();
    }, [reload]);

    const myComplaints = useMemo(
        () => (Array.isArray(complainsList) ? complainsList : []),
        [complainsList]
    );

    const rows = useMemo(
        () =>
            [...myComplaints]
                .sort((a, b) => new Date(b.date) - new Date(a.date))
                .map((c) => ({
                    id: c._id || c.id,
                    complaintText: c.complaint?.trim() || "—",
                    dateLabel: formatDate(c.date),
                })),
        [myComplaints]
    );

    const paginated = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    useEffect(() => {
        if (status === "added") {
            setSubmitting(false);
            setComplaint("");
            setDate(new Date().toISOString().slice(0, 10));
            setMessage("Your complaint was submitted to the school administration.");
            setShowPopup(true);
            reload();
            dispatch(underControl());
        } else if (error && submitting) {
            setSubmitting(false);
            setMessage(typeof error === "string" ? error : "Could not submit complaint. Please try again.");
            setShowPopup(true);
            dispatch(underControl());
        }
    }, [status, error, submitting, dispatch, reload]);

    const handleSubmit = (event) => {
        event.preventDefault();
        if (!teacherId || !schoolId) {
            setMessage("Missing teacher or school information.");
            setShowPopup(true);
            return;
        }
        if (!date || !String(complaint).trim()) {
            setMessage("Date and complaint details are required.");
            setShowPopup(true);
            return;
        }

        setSubmitting(true);
        dispatch(
            addStuff(
                {
                    teacher: teacherId,
                    school: schoolId,
                    date,
                    complaint: String(complaint).trim(),
                },
                "Complain"
            )
        );
    };

    return (
        <>
            <Box
                sx={{
                    minHeight: "100%",
                    py: { xs: 2, sm: 3 },
                    background: (t) =>
                        t.palette.mode === "light"
                            ? `linear-gradient(180deg, ${alpha(t.palette.warning.main, 0.05)} 0%, ${t.palette.grey[100]} 22%)`
                            : alpha(t.palette.common.black, 0.12),
                }}
            >
                <Container maxWidth="lg">
                    <Paper
                        elevation={0}
                        sx={{
                            p: { xs: 2.5, sm: 3 },
                            mb: 3,
                            borderRadius: 3,
                            color: "common.white",
                            background: (t) =>
                                `linear-gradient(120deg, ${t.palette.grey[900]} 0%, ${t.palette.warning.dark} 55%, ${t.palette.warning.main} 100%)`,
                        }}
                    >
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
                            <Avatar
                                sx={{
                                    width: 56,
                                    height: 56,
                                    bgcolor: alpha("#fff", 0.14),
                                }}
                            >
                                <ReportProblemOutlinedIcon />
                            </Avatar>
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="overline" sx={{ opacity: 0.9, letterSpacing: 1 }}>
                                    Staff support
                                </Typography>
                                <Typography variant="h4" fontWeight={800} lineHeight={1.15}>
                                    Lodge a complaint
                                </Typography>
                                <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.75, maxWidth: 560 }}>
                                    Report facilities, safety, or administrative issues to your school director. Submissions
                                    are reviewed in the admin complaints inbox.
                                </Typography>
                            </Box>
                            <Chip
                                label={`${rows.length} submission${rows.length === 1 ? "" : "s"}`}
                                sx={{
                                    bgcolor: alpha("#fff", 0.14),
                                    color: "inherit",
                                    fontWeight: 700,
                                }}
                            />
                        </Stack>
                    </Paper>

                    <Grid container spacing={3}>
                        <Grid item xs={12} md={5}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: { xs: 2, sm: 2.5 },
                                    borderRadius: 2.5,
                                    border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                                }}
                            >
                                <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    sx={{ mb: 2 }}
                                >
                                    <Typography variant="h6" fontWeight={700}>
                                        New complaint
                                    </Typography>
                                    <Button size="small" onClick={() => setShowForm((v) => !v)}>
                                        {showForm ? "Hide" : "Show"}
                                    </Button>
                                </Stack>
                                <Collapse in={showForm}>
                                    <Box component="form" onSubmit={handleSubmit}>
                                        <Stack spacing={2}>
                                            <TextField
                                                fullWidth
                                                label="Incident date"
                                                type="date"
                                                value={date}
                                                onChange={(e) => setDate(e.target.value)}
                                                required
                                                InputLabelProps={{ shrink: true }}
                                            />
                                            <TextField
                                                fullWidth
                                                label="Describe the issue"
                                                placeholder="What happened, where, and who was affected?"
                                                value={complaint}
                                                onChange={(e) => setComplaint(e.target.value)}
                                                required
                                                multiline
                                                minRows={4}
                                                maxRows={8}
                                            />
                                            <Button
                                                type="submit"
                                                variant="contained"
                                                size="large"
                                                startIcon={
                                                    submitting ? (
                                                        <CircularProgress size={18} color="inherit" />
                                                    ) : (
                                                        <SendOutlinedIcon />
                                                    )
                                                }
                                                disabled={submitting}
                                                sx={{ fontWeight: 700 }}
                                            >
                                                {submitting ? "Submitting…" : "Submit to administration"}
                                            </Button>
                                        </Stack>
                                    </Box>
                                </Collapse>
                            </Paper>
                        </Grid>

                        <Grid item xs={12} md={7}>
                            <Paper
                                elevation={0}
                                sx={{
                                    borderRadius: 2.5,
                                    overflow: "hidden",
                                    border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                                }}
                            >
                                <Box
                                    sx={{
                                        px: 2.5,
                                        py: 2,
                                        borderBottom: 1,
                                        borderColor: "divider",
                                        bgcolor: (t) => alpha(t.palette.warning.main, 0.06),
                                    }}
                                >
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <HistoryOutlinedIcon color="action" fontSize="small" />
                                        <Typography variant="h6" fontWeight={700}>
                                            Your submissions
                                        </Typography>
                                    </Stack>
                                </Box>

                                {listError && (
                                    <Box sx={{ p: 2 }}>
                                        <Typography color="error" variant="body2">
                                            Could not load your complaint history.
                                        </Typography>
                                    </Box>
                                )}

                                {loading && rows.length === 0 ? (
                                    <Box sx={{ p: 2 }}>
                                        {[1, 2, 3].map((i) => (
                                            <Skeleton key={i} height={52} sx={{ mb: 1 }} />
                                        ))}
                                    </Box>
                                ) : rows.length === 0 ? (
                                    <Box sx={{ py: 5, px: 2, textAlign: "center" }}>
                                        <ReportProblemOutlinedIcon
                                            sx={{ fontSize: 44, color: "text.disabled", mb: 1 }}
                                        />
                                        <Typography color="text.secondary">
                                            You have not submitted any complaints yet.
                                        </Typography>
                                    </Box>
                                ) : (
                                    <>
                                        <TableContainer>
                                            <Table size="small">
                                                <TableHead>
                                                    <StyledTableRow>
                                                        <StyledTableCell>Date</StyledTableCell>
                                                        <StyledTableCell>Complaint</StyledTableCell>
                                                    </StyledTableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {paginated.map((row) => (
                                                        <StyledTableRow key={row.id} hover>
                                                            <StyledTableCell sx={{ whiteSpace: "nowrap" }}>
                                                                <Chip size="small" label={row.dateLabel} variant="outlined" />
                                                            </StyledTableCell>
                                                            <StyledTableCell>
                                                                <Typography
                                                                    variant="body2"
                                                                    color="text.secondary"
                                                                    sx={{ whiteSpace: "pre-wrap" }}
                                                                >
                                                                    {row.complaintText}
                                                                </Typography>
                                                            </StyledTableCell>
                                                        </StyledTableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                        <TablePagination
                                            count={rows.length}
                                            page={page}
                                            rowsPerPage={rowsPerPage}
                                            onPageChange={(_e, p) => setPage(p)}
                                            onRowsPerPageChange={(e) => {
                                                setRowsPerPage(parseInt(e.target.value, 10));
                                                setPage(0);
                                            }}
                                            rowsPerPageOptions={[5, 10, 25]}
                                        />
                                    </>
                                )}
                            </Paper>
                        </Grid>
                    </Grid>
                </Container>
            </Box>
            <Popup message={message} setShowPopup={setShowPopup} showPopup={showPopup} />
        </>
    );
};

export default TeacherComplain;
